import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtIDR, fmtDate, RATE_META } from "@/lib_app/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import Avatar from "@/components/app/Avatar";
import { PeriodFilter } from "./SalesReport";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceDot, CartesianGrid } from "recharts";
import { Download, Trophy, TrendingUp, TrendingDown, Target as TargetIcon, Users as UsersIcon, Percent, Clock, Star, ListChecks, ChevronDown, ChevronRight, Activity, ArrowUpRight, ArrowDownRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const ID_MONTHS = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
function formatIndoDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("T")[0].split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${ID_MONTHS[m - 1]} ${y}`;
}

const CAT_LABEL = { pemerintah: "Pemerintah", bumn: "BUMN", swasta: "Swasta", partner: "Partner" };
const CAT_TINT = {
  pemerintah: "bg-red-100 text-red-700",
  bumn: "bg-blue-100 text-blue-700",
  swasta: "bg-[#C9F0D6] text-[#4A6D53]",
  partner: "bg-[#DCE0E7] text-[#626061]",
};

export default function TeamReport() {
  const { user: me } = useAuth();
  const isOwner = me?.role === "owner";
  const isDirector = me?.role === "sales_director";
  const isTeamLead = isOwner || isDirector;
  // View mode for team leads: "personal" (own rapor) | "team" (cards grid)
  const [leadView, setLeadView] = useState(isOwner ? "team" : "personal");
  useEffect(() => { if (me?.role === "owner") setLeadView("team"); /* eslint-disable-next-line */ }, [me?.role]);
  const [users, setUsers] = useState([]);
  const [targetUserId, setTargetUserId] = useState(me?.role === "owner" ? "" : (me?.user_id || ""));
  const [period, setPeriod] = useState("ytd");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([new Date().getFullYear()]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ownerComment, setOwnerComment] = useState("");
  const [exporting, setExporting] = useState(false);
  const [detailTab, setDetailTab] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/reports/years").then((r) => {
      const arr = r.data?.years || [];
      if (arr.length) {
        setYears(arr);
        if (!arr.includes(year)) setYear(arr[0]);
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!targetUserId && me?.user_id && !isTeamLead) setTargetUserId(me.user_id);
    // Director default: personal → their own rapor
    if (isDirector && leadView === "personal" && !targetUserId && me?.user_id) setTargetUserId(me.user_id);
  }, [me, targetUserId, isTeamLead, isDirector, leadView]);

  // Team leads: quick metrics per team member (used in team card grid)
  const [teamMetrics, setTeamMetrics] = useState({});
  useEffect(() => {
    if (!isTeamLead) return;
    api.get(`/analytics/summary?year=${year}`).then((r) => {
      const map = {};
      (r.data?.ranking || []).forEach((row) => {
        map[row.user_id] = {
          achievement_pct: row.achievement_pct || 0,
          deal_value: row.realization || 0,
          deal_count: row.won || 0,
          prospect_count: (row.won || 0) + (row.active || 0) + (row.lost || 0),
          target: row.target || 0,
        };
      });
      setTeamMetrics(map);
    }).catch(() => {});
  }, [isTeamLead, year]);

  // Selected user object + whether it's an owner (owner has no rapor)
  const selectedUser = useMemo(() => users.find((u) => u.user_id === targetUserId) || null, [users, targetUserId]);
  const selectedIsOwner = selectedUser?.role === "owner";

  const load = async () => {
    if (!targetUserId) return;
    if (selectedIsOwner) { setData(null); return; }
    setBusy(true);
    try {
      const params = { period };
      if (period === "custom") { params.start = start; params.end = end; }
      if (period === "year") { params.year = year; }
      const r = await api.get(`/reports/team/${targetUserId}`, { params });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memuat rapor");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [targetUserId, period, year, selectedIsOwner]);

  const exportPdf = async () => {
    if (!isTeamLead) return;
    setExporting(true);
    try {
      const body = { period, owner_comment: ownerComment };
      if (period === "custom") { body.start = start; body.end = end; }
      if (period === "year") { body.year = year; }
      const r = await api.post(`/reports/team/${targetUserId}/pdf`, body, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // parse filename from Content-Disposition
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename\*=UTF-8''([^;]+)/);
      a.download = m ? decodeURIComponent(m[1]) : `Rapor_${data?.user?.nickname || "sales"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF berhasil di-generate");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal export PDF");
    } finally { setExporting(false); }
  };

  const selectableUsers = useMemo(() => users.filter((u) => u.is_active !== false && u.role !== "finance"), [users]);

  return (
    <div className="space-y-6" data-testid="team-report-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Laporan</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">
            {isTeamLead
              ? (leadView === "team" && !targetUserId
                  ? "Laporan Tim"
                  : (targetUserId && selectedUser
                      ? (targetUserId === me?.user_id ? "Rapor Saya" : `Rapor · ${selectedUser.nickname || selectedUser.name}`)
                      : "Laporan Tim"))
              : "Rapor Saya"}
          </h1>
          <p className="text-sm text-[#A0A3A8] mt-1">
            {isTeamLead
              ? (leadView === "team" && !targetUserId
                  ? `Ringkasan tim tahun ${year} — klik kartu anggota untuk lihat rapor lengkap.`
                  : "Rapor performa personal — siap di-export sebagai PDF penilaian.")
              : "Ringkasan performa Anda untuk periode terpilih."}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {/* Sales Director: tab "Rapor Saya" | "Rapor Tim" — visible on personal own rapor & team grid */}
          {isDirector && (leadView === "team" || targetUserId === me?.user_id) && (
            <div className="inline-flex items-center rounded-full bg-white card-soft p-1" data-testid="lead-view-toggle">
              <button
                onClick={() => { setLeadView("personal"); setTargetUserId(me?.user_id || ""); }}
                data-testid="lead-view-personal"
                className={`inline-flex items-center h-9 px-4 rounded-full text-xs font-semibold transition-all ${
                  leadView === "personal" ? "bg-[#090808] text-white shadow-[0_4px_10px_rgba(9,8,8,0.20)]" : "text-[#626061] hover:text-[#090808]"
                }`}
              >
                Rapor Saya
              </button>
              <button
                onClick={() => { setLeadView("team"); setTargetUserId(""); setData(null); }}
                data-testid="lead-view-team"
                className={`inline-flex items-center h-9 px-4 rounded-full text-xs font-semibold transition-all ${
                  leadView === "team" ? "bg-[#090808] text-white" : "text-[#626061] hover:text-[#090808]"
                }`}
              >
                Rapor Tim
              </button>
            </div>
          )}
          {isTeamLead && targetUserId && targetUserId !== me?.user_id && (
            <Button
              variant="outline"
              onClick={() => { setTargetUserId(isDirector && leadView === "personal" ? (me?.user_id || "") : ""); setData(null); if (isDirector) setLeadView("team"); }}
              data-testid="back-to-team-list"
              className="h-11 rounded-full bg-white card-soft border-transparent gap-2 px-5"
            >
              <ArrowLeft className="w-4 h-4" /> Kembali ke Daftar Tim
            </Button>
          )}
          {(!isTeamLead || targetUserId || (isDirector && leadView === "personal")) && (
            <PeriodFilter
              period={period} setPeriod={setPeriod}
              start={start} setStart={setStart}
              end={end} setEnd={setEnd}
              year={year} setYear={setYear} years={years}
              onApply={load}
            />
          )}
        </div>
      </div>

      {busy && <div className="text-sm text-[#A0A3A8]">Memuat rapor...</div>}
      {!data && !busy && !isOwner && <div className="text-sm text-[#A0A3A8]">Memuat rapor...</div>}

      {/* Team leads default: Team Cards Grid */}
      {isTeamLead && leadView === "team" && !targetUserId && (
        <div data-testid="team-cards-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {selectableUsers.filter((u) => u.role !== "owner" && u.user_id !== me?.user_id).map((u) => {
            const m = teamMetrics[u.user_id] || {};
            const pct = m.achievement_pct || 0;
            const pctColor = pct >= 100 ? "#57D28A" : pct >= 60 ? "#B39548" : pct > 0 ? "#626061" : "#A0A3A8";
            const barColor = pct >= 100 ? "#57D28A" : pct >= 60 ? "#F0B840" : "#CE6968";
            const roleLabel = u.role === "sales_director" ? "Sales Director" : u.role === "sales" ? "Sales" : (u.role || "");
            return (
              <button
                key={u.user_id}
                type="button"
                data-testid={`team-card-${u.user_id}`}
                onClick={() => setTargetUserId(u.user_id)}
                className="text-left rounded-3xl p-6 transition-all hover:shadow-[0_18px_40px_-12px_rgba(30,64,175,0.30)] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#2E5BFF]/30"
                style={{
                  background:
                    "radial-gradient(600px 400px at 100% 0%, rgba(107,143,226,0.28) 0%, transparent 60%)," +
                    "radial-gradient(500px 350px at 0% 100%, rgba(151,173,212,0.24) 0%, transparent 60%)," +
                    "linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(232,240,254,0.65) 100%)",
                  backdropFilter: "blur(24px) saturate(180%)",
                  WebkitBackdropFilter: "blur(24px) saturate(180%)",
                  border: "1px solid rgba(107,143,226,0.28)",
                  boxShadow: "0 12px 28px -14px rgba(30,64,175,0.22), inset 0 1px 0 rgba(255,255,255,0.55)",
                }}
              >
                <div className="flex items-center gap-3">
                  <Avatar name={u.name} picture={u.picture} size="md" className="!w-14 !h-14 !text-base !border-2 !border-white/70" />
                  <div className="min-w-0 flex-1">
                    <div className="text-lg font-bold text-[#090808] truncate">{u.nickname || u.name}</div>
                    <div className="text-xs text-[#3B4A63] mt-0.5">{roleLabel}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#2E5BFF] shrink-0" />
                </div>

                {/* Achievement progress bar */}
                <div className="mt-5">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-[10px] uppercase tracking-widest text-[#626061] font-semibold">Target Tercapai</span>
                    <span className="text-lg font-bold tabular-nums" style={{ color: pctColor }}>{pct.toFixed(2)}%</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden bg-white/60">
                    <div className="h-full transition-all rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: barColor }} />
                  </div>
                  <div className="flex items-baseline justify-between mt-1.5 text-[11px] text-[#3B4A63]">
                    <span>
                      Achieved <span className="font-num font-semibold text-[#57D28A] tabular-nums">Rp {Math.round(m.deal_value || 0).toLocaleString("id-ID")}</span>
                    </span>
                    <span>
                      Target <span className="font-num font-semibold text-[#090808] tabular-nums">Rp {Math.round(m.target || 0).toLocaleString("id-ID")}</span>
                    </span>
                  </div>
                </div>

                {/* Mini stats: Deal count · Prospek count */}
                <div className="mt-4 pt-4 border-t border-white/50 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#626061] font-semibold">Deal</div>
                    <div className="text-base font-bold text-[#090808] tabular-nums">{m.deal_count || 0}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#626061] font-semibold">Total Prospek</div>
                    <div className="text-base font-bold text-[#090808] tabular-nums">{m.prospect_count || 0}</div>
                  </div>
                </div>
              </button>
            );
          })}
          {selectableUsers.filter((u) => u.role !== "owner" && u.user_id !== me?.user_id).length === 0 && (
            <div className="col-span-full text-center text-sm text-[#A0A3A8] py-16">Belum ada anggota tim aktif.</div>
          )}
        </div>
      )}

      {data && !selectedIsOwner && (
        <>
          {/* HEADER — glassy with vibrant blue accent to highlight profile */}
          <Card
            className="border-0 rounded-3xl overflow-hidden relative"
            data-testid="report-header"
            style={{
              background:
                "radial-gradient(600px 400px at 100% 0%, rgba(107,143,226,0.35) 0%, transparent 60%)," +
                "radial-gradient(500px 350px at 0% 100%, rgba(151,173,212,0.30) 0%, transparent 60%)," +
                "linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(230,238,252,0.65) 100%)",
              backdropFilter: "blur(28px) saturate(180%)",
              WebkitBackdropFilter: "blur(28px) saturate(180%)",
              border: "1px solid rgba(255,255,255,0.75)",
              boxShadow: "0 30px 60px -20px rgba(30,64,175,0.20), 0 8px 24px -12px rgba(20,22,31,0.10)",
            }}
          >
            {/* Subtle giant rank number as decorative watermark */}
            <div className="absolute right-8 top-1/2 -translate-y-1/2 text-[240px] font-black leading-none text-[#090808]/[0.05] select-none pointer-events-none tracking-tighter">
              #{data.rank.position || "-"}
            </div>

            <div className="p-7 md:p-9 relative z-10 flex flex-col md:flex-row md:items-center gap-8">
              {/* Avatar — square rounded, LARGER HD */}
              <div className="shrink-0">
                {data.user.picture ? (
                  <img
                    src={data.user.picture}
                    alt={data.user.name}
                    className="w-44 h-44 md:w-52 md:h-52 rounded-3xl object-cover ring-2 ring-white shadow-[0_24px_50px_-14px_rgba(30,64,175,0.35)]"
                  />
                ) : (
                  <div className="w-44 h-44 md:w-52 md:h-52 rounded-3xl bg-gradient-to-br from-white to-[#DCE0E7] ring-2 ring-white shadow-[0_24px_50px_-14px_rgba(30,64,175,0.35)] flex items-center justify-center text-6xl md:text-7xl font-black text-[#090808] font-display">
                    {(data.user.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Info stack */}
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center h-5 px-2.5 rounded-full bg-[#090808] text-white text-[9px] font-bold uppercase tracking-[0.2em]">
                    Rapor Sales
                  </span>
                  <span className="inline-flex items-center h-5 px-2.5 rounded-full bg-white/70 text-[#626061] text-[9px] font-bold uppercase tracking-[0.2em] whitespace-nowrap">
                    {(data.user.role || "sales").replace("_", " ")}
                  </span>
                </div>
                <h2 className="text-5xl md:text-6xl font-black leading-[1.05] tracking-tight text-[#090808] font-display">
                  {data.user.name}
                </h2>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#626061] pt-2">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">Periode</span>
                    <span className="font-num font-semibold text-[#090808]">{formatIndoDate(data.period.start)} → {formatIndoDate(data.period.end)}</span>
                  </span>
                  {data.user.email && (
                    <>
                      <span className="text-[#A0A3A8]">|</span>
                      <span className="font-semibold text-[#090808]">{data.user.email}</span>
                    </>
                  )}
                  {data.user.phone && (
                    <>
                      <span className="text-[#A0A3A8]">|</span>
                      <span className="font-num font-semibold text-[#090808]">{data.user.phone}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Ranking on the right — subtle */}
              <div className="shrink-0 flex md:flex-col items-center md:items-end gap-3 md:gap-1">
                <div className="text-right">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-[#A0A3A8] font-bold mb-1">Ranking</div>
                  <div className="flex items-baseline gap-1 justify-end">
                    <span className="text-5xl md:text-6xl font-black leading-none text-[#090808] font-num">#{data.rank.position || "-"}</span>
                    <span className="text-[#A0A3A8] text-sm font-num">/ {data.rank.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* KPI 4x2 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniKPI label="Deal Value" value={fmtIDR(data.kpi.deal_value)} icon={<Trophy className="w-4 h-4" />} sub={<Delta v={data.comparison.delta_pct} />} testid="kpi-deal-value" />
            <MiniKPI label="Target" value={fmtIDR(data.target.period_target)} icon={<TargetIcon className="w-4 h-4" />} accent="violet" sub={<span className="text-[10px] text-[#A0A3A8]">akumulasi periode</span>} testid="kpi-target" />
            <MiniKPI label="Deal Count" value={data.kpi.deal_count} icon={<Trophy className="w-4 h-4" />} sub={<span className="text-[10px] text-[#A0A3A8]">Lost: {data.kpi.lost_count}</span>} />
            <MiniKPI label="Total Prospek" value={data.kpi.total_prospects} icon={<UsersIcon className="w-4 h-4" />} sub={<span className="text-[10px] text-[#A0A3A8]">Aktif: {data.kpi.active_count}</span>} />
            <MiniKPI
              label="Target Tercapai"
              value={`${data.target.achieved_pct.toFixed(2)}%`}
              icon={<TargetIcon className="w-4 h-4" />}
              accent={data.target.achieved_pct >= 100 ? "emerald" : data.target.achieved_pct >= 70 ? "amber" : "rose"}
              tier={data.target.achieved_pct}
              testid="kpi-achievement"
            />
            <MiniKPI label="Rata-rata Deal" value={fmtIDR(data.kpi.avg_deal_value)} icon={<Trophy className="w-4 h-4" />} accent="violet" />
            <MiniKPI label="Klien Baru" value={data.new_clients.count} icon={<UsersIcon className="w-4 h-4" />} accent="amber" testid="kpi-new-clients" />
            <MiniKPI label="Win Rate" value={`${data.kpi.win_rate.toFixed(2)}%`} icon={<Percent className="w-4 h-4" />} accent="emerald" testid="kpi-win-rate" />
          </div>

          {/* Target Progress */}
          <Card className="p-5 border-0 rounded-2xl card-soft">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-[#090808] flex items-center gap-1.5"><TargetIcon className="w-4 h-4 text-[#090808]" /> Progress Target</h3>
              <span className="text-xs text-[#A0A3A8] font-num">{fmtIDR(data.kpi.deal_value)} / {fmtIDR(data.target.period_target)}</span>
            </div>
            <div className="relative h-4 bg-[#F4F5F7] rounded-full overflow-hidden">
              <div
                className={`absolute h-full rounded-full ${
                  data.target.achieved_pct >= 100 ? "bg-[#57D28A]" : data.target.achieved_pct >= 70 ? "bg-[#FFDB52]" : "bg-[#FF6B7D]"
                }`}
                style={{ width: `${Math.min(data.target.achieved_pct, 100)}%` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-[10px] text-[#A0A3A8] font-semibold">
              <span>0%</span>
              <span>{data.target.achieved_pct.toFixed(2)}%</span>
              <span>100%</span>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Top 5 Deals */}
            <Card className="p-5 border-0 rounded-2xl card-soft">
              <h3 className="text-sm font-bold text-[#090808] mb-3 flex items-center gap-1.5"><Star className="w-4 h-4 text-[#FFDB52]" /> Top 5 Deal</h3>
              {data.top_deals.length === 0 ? (
                <div className="text-sm text-[#A0A3A8] text-center py-8">Belum ada deal periode ini.</div>
              ) : (
                <div className="space-y-2">
                  {data.top_deals.map((d, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-[#DCE0E7] last:border-0">
                      <div className={`w-7 h-7 rounded-full font-bold text-xs flex items-center justify-center ${i === 0 ? "bg-[#FFE89A] text-[#B39548]" : i === 1 ? "bg-[#B9C5D8] text-[#626061]" : i === 2 ? "bg-[#FFD7B5] text-orange-600" : "bg-[#F4F5F7] text-[#A0A3A8]"}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-[#090808] truncate">{d.customer}</div>
                        <div className="text-[11px] text-[#A0A3A8] truncate">{d.service} · {fmtDate(d.deal_date)}</div>
                      </div>
                      <div className="text-sm font-bold text-[#57D28A] tabular-nums shrink-0">{fmtIDR(d.value)}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Trend Achievement 12 Bulan */}
            <TrendCard monthly={data.monthly_trend || []} />
          </div>

          {/* Detail Prospek — Tab filter: Semua / Deal / Aktif / Lost */}
          <DetailProspek data={data} tab={detailTab} setTab={setDetailTab} onRowClick={(id) => navigate(`/prospects?open=${id}`)} />

          {/* Team Lead Comment + Export (owner always; director only for others) */}
          {isTeamLead && targetUserId && targetUserId !== me?.user_id && (
            <Card className="p-5 border-0 rounded-2xl card-soft" data-testid="owner-comment-section">
              <h3 className="text-sm font-bold text-[#090808] mb-2">Komentar & Penilaian {isOwner ? "Owner" : "Sales Director"}</h3>
              <Textarea
                data-testid="owner-comment-input"
                value={ownerComment}
                onChange={(e) => setOwnerComment(e.target.value)}
                placeholder="Tulis penilaian personal untuk sales ini. Akan ikut ke PDF."
                rows={4}
                className="text-sm"
              />
              <div className="flex justify-end mt-3">
                <Button
                  onClick={exportPdf}
                  disabled={exporting || !data}
                  data-testid="export-pdf-btn"
                  className="gap-2 h-11 rounded-full bg-[#090808] hover:bg-[#626061] px-6 font-semibold"
                >
                  <Download className="w-4 h-4" /> {exporting ? "Membuat PDF..." : "Export PDF Rapor"}
                </Button>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function MiniKPI({ label, value, icon, sub, accent = "violet", tier, testid }) {
  const tint = {
    violet: "bg-[#DCE0E7] text-[#090808]",
    emerald: "bg-[#C9F0D6] text-[#57D28A]",
    amber: "bg-[#FFE89A] text-[#B39548]",
    rose: "bg-[#FFD7B5] text-[#FF6B7D]",
    slate: "bg-[#DCE0E7] text-[#626061]",
  }[accent];
  // Tier background: pastel highlight based on how close `tier` (a %) is to 100.
  //   >= 100 → hijau muda, 70–99 → kuning muda, 40–69 → oranye muda, < 40 → merah muda
  let tierBg = "";
  if (typeof tier === "number") {
    if (tier >= 100) tierBg = "bg-emerald-50 ring-1 ring-emerald-100";
    else if (tier >= 70) tierBg = "bg-amber-50 ring-1 ring-amber-100";
    else if (tier >= 40) tierBg = "bg-orange-50 ring-1 ring-orange-100";
    else tierBg = "bg-[#F8ECE5] ring-1 ring-rose-100";
  }
  return (
    <Card className={`p-4 border-0 rounded-2xl card-soft transition-colors ${tierBg}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">{label}</span>
        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${tint}`}>{icon}</div>
      </div>
      <div className="text-lg font-bold text-[#090808]">{value}</div>
      {sub && <div className="mt-0.5">{sub}</div>}
    </Card>
  );
}

function Delta({ v }) {
  const pos = v >= 0;
  const Icon = pos ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-bold ${pos ? "text-[#57D28A] bg-emerald-50" : "text-[#FF6B7D] bg-[#F8ECE5]"}`}>
      <Icon className="w-3 h-3" /> {pos ? "+" : ""}{v.toFixed(2)}%
    </span>
  );
}

const MONTH_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
const fmtMonth = (pm) => { if (!pm) return "-"; const [y, m] = pm.split("-"); return `${MONTH_ID[Number(m) - 1] || m} ${y}`; };

function TrendCard({ monthly }) {
  const rows = monthly || [];
  const MONTH_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
  const data = rows.map((m, i) => ({ ...m, label: MONTH_ID[i] || m.month }));
  const anyData = data.some((d) => d.achievement > 0 || d.target > 0);
  const totalAch = data.reduce((s, d) => s + d.achievement, 0);
  const totalTgt = data.reduce((s, d) => s + d.target, 0);
  const overallPct = totalTgt > 0 ? Math.round(totalAch * 100 / totalTgt) : 0;
  // Best & worst month by achievement (non-zero months preferred)
  const nonZero = data.filter((d) => d.achievement > 0);
  const best  = nonZero.length ? nonZero.reduce((a, b) => (b.achievement > a.achievement ? b : a)) : null;
  const worst = nonZero.length > 1 ? nonZero.reduce((a, b) => (b.achievement < a.achievement ? b : a)) : null;
  // Peak achievement of any month (for reference)
  const peak = best ? best.achievement : 0;
  // Trend vs previous month (compare last two non-zero months)
  const last = data[data.length - 1];
  const prev = data[data.length - 2];
  let momentum = 0;
  if (prev && prev.achievement > 0) momentum = ((last.achievement - prev.achievement) / prev.achievement) * 100;

  const fmtCompact = (n) => {
    const v = Number(n) || 0;
    if (v >= 1e9) return `${(v / 1e9).toFixed(0)}M`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(0)}Jt`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(0)}rb`;
    return String(v);
  };

  return (
    <Card className="p-5 border-0 rounded-2xl card-soft" data-testid="trend-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-[#090808] flex items-center gap-1.5"><Activity className="w-4 h-4 text-[#090808]" /> Trend Achievement 12 Bulan</h3>
        {anyData && (
          <span className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-bold ${
            momentum >= 0 ? "text-[#57D28A] bg-emerald-50" : "text-[#FF6B7D] bg-[#F8ECE5]"
          }`}>
            {momentum >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {momentum >= 0 ? "+" : ""}{momentum.toFixed(2)}% vs bulan lalu
          </span>
        )}
      </div>
      {!anyData ? (
        <div className="text-sm text-[#A0A3A8] text-center py-10">Belum ada realisasi deal tahun ini.</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="rounded-xl bg-[#F4F5F7] px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Overall</div>
              <div className={`text-sm font-bold tabular-nums ${overallPct >= 100 ? "text-[#57D28A]" : overallPct >= 70 ? "text-[#B39548]" : "text-[#090808]"}`}>{overallPct}%</div>
            </div>
            {best && (
              <div className="rounded-xl bg-emerald-50 px-2.5 py-2">
                <div className="text-[9px] uppercase tracking-widest text-[#4A6D53] font-semibold">Bulan terbaik</div>
                <div className="text-sm font-bold text-[#4A6D53] tabular-nums">{best.label} · {fmtCompact(best.achievement)}</div>
              </div>
            )}
            {worst ? (
              <div className="rounded-xl bg-[#F8ECE5] px-2.5 py-2">
                <div className="text-[9px] uppercase tracking-widest text-[#FF6B7D] font-semibold">Bulan terendah</div>
                <div className="text-sm font-bold text-[#FF6B7D] tabular-nums">{worst.label} · {fmtCompact(worst.achievement)}</div>
              </div>
            ) : (
              <div className="rounded-xl bg-[#F4F5F7] px-2.5 py-2">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Peak</div>
                <div className="text-sm font-bold text-[#090808] tabular-nums">{fmtCompact(peak)}</div>
              </div>
            )}
          </div>
          <div className="h-32 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: 6 }}>
                <defs>
                  <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#090808" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#090808" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#DCE0E7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#A0A3A8" }} axisLine={false} tickLine={false} />
                <YAxis hide domain={[0, "auto"]} />
                <Tooltip
                  formatter={(v, n) => [fmtIDR(v), n === "achievement" ? "Achievement" : "Target"]}
                  labelFormatter={(l) => `Bulan ${l}`}
                  contentStyle={{ borderRadius: 12, border: "1px solid #DCE0E7", fontSize: 11 }}
                />
                <Area type="monotone" dataKey="achievement" stroke="#090808" strokeWidth={2} fill="url(#trendGrad)" />
                {best && <ReferenceDot x={best.label} y={best.achievement} r={5} fill="#57D28A" stroke="#fff" strokeWidth={2} />}
                {worst && <ReferenceDot x={worst.label} y={worst.achievement} r={5} fill="#FF6B7D" stroke="#fff" strokeWidth={2} />}
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-6 md:grid-cols-12 gap-1 pt-2">
            {data.map((d, i) => {
              const isBest = best && best.label === d.label;
              const isWorst = worst && worst.label === d.label;
              return (
                <div
                  key={i}
                  className={`text-center rounded-lg py-1 ${isBest ? "bg-emerald-50 ring-1 ring-emerald-200" : isWorst ? "bg-[#F8ECE5] ring-1 ring-rose-200" : "bg-[#F4F5F7]"}`}
                  title={`${d.label}: ${fmtIDR(d.achievement)} / target ${fmtIDR(d.target)}`}
                >
                  <div className="text-[8px] uppercase tracking-widest text-[#A0A3A8] font-semibold">{d.label}</div>
                  <div className={`text-[10px] font-bold tabular-nums ${d.pct >= 100 ? "text-[#57D28A]" : d.pct >= 60 ? "text-[#B39548]" : d.pct > 0 ? "text-[#090808]" : "text-[#A0A3A8]"}`}>{d.pct}%</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}


function DetailProspek({ data, tab, setTab, onRowClick }) {
  const pd = data?.prospects_detail || { deal: [], active: [], lost: [], subtotals: {} };
  // Auto-collapse when the section is huge; keep small ones open.
  const [openMap, setOpenMap] = useState({
    deal: pd.deal.length <= 10,
    active: pd.active.length <= 10,
    lost: pd.lost.length <= 10,
  });
  const toggle = (k) => setOpenMap((m) => ({ ...m, [k]: !m[k] }));
  const TABS = [
    { key: "all",    label: "Semua",         count: pd.deal.length + pd.active.length + pd.lost.length },
    { key: "deal",   label: "Deal (Won)",    count: pd.deal.length,    color: "text-[#57D28A] border-emerald-500" },
    { key: "active", label: "Aktif",         count: pd.active.length,  color: "text-[#090808] border-[#090808]" },
    { key: "lost",   label: "Lost",          count: pd.lost.length,    color: "text-[#FF6B7D] border-rose-500" },
  ];
  const sections = [];
  if (tab === "all" || tab === "deal")   sections.push({ key: "deal",   label: "Deal (Won)",    rows: pd.deal,   valField: "realization",    subtotal: pd.subtotals?.deal_value || 0,   accent: "emerald" });
  if (tab === "all" || tab === "active") sections.push({ key: "active", label: "Prospek Aktif", rows: pd.active, valField: "prospect_value", subtotal: pd.subtotals?.active_value || 0, accent: "violet" });
  if (tab === "all" || tab === "lost")   sections.push({ key: "lost",   label: "Lost",          rows: pd.lost,   valField: "prospect_value", subtotal: pd.subtotals?.lost_value || 0,   accent: "rose" });
  const total = pd.deal.length + pd.active.length + pd.lost.length;
  return (
    <Card className="border-0 rounded-2xl card-soft overflow-hidden" data-testid="detail-prospek">
      <div className="px-5 pt-5 pb-3 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-[#DCE0E7] text-[#090808] flex items-center justify-center"><ListChecks className="w-4 h-4" /></div>
          <div>
            <h3 className="text-sm font-bold text-[#090808]">Detail Prospek</h3>
            <p className="text-[11px] text-[#A0A3A8]">{total} prospek dalam periode ini · klik baris untuk buka</p>
          </div>
        </div>
        <div className="inline-flex bg-[#F4F5F7] rounded-full p-1 gap-1" data-testid="detail-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`detail-tab-${t.key}`}
              className={`h-8 px-3 rounded-full text-[11px] font-bold transition-colors ${
                tab === t.key ? "bg-[#090808] text-white shadow-sm" : "text-[#626061] hover:text-[#090808]"
              }`}
            >{t.label} <span className="opacity-70 ml-0.5">({t.count})</span></button>
          ))}
        </div>
      </div>

      {total === 0 && (
        <div className="text-sm text-[#A0A3A8] text-center py-10 border-t border-[#DCE0E7]">Tidak ada prospek pada periode ini.</div>
      )}

      {sections.map((s) => {
        const accentTint = { emerald: "text-[#57D28A] bg-emerald-50 border-[#C9F0D6]", violet: "text-[#090808] bg-[#DCE0E7] border-[#DDD3FF]", rose: "text-[#FF6B7D] bg-[#F8ECE5] border-rose-200" }[s.accent];
        const accentBar = { emerald: "bg-[#57D28A]", violet: "bg-[#090808]", rose: "bg-[#FF6B7D]" }[s.accent];
        const isOpen = !!openMap[s.key];
        return (
          <div key={s.key} className="border-t border-[#DCE0E7]" data-testid={`detail-section-${s.key}`}>
            <button
              type="button"
              onClick={() => toggle(s.key)}
              data-testid={`detail-section-toggle-${s.key}`}
              aria-expanded={isOpen}
              className={`w-full px-5 py-2.5 flex items-center justify-between text-xs font-bold ${accentTint} border-y hover:brightness-95 transition-all`}
            >
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                <span className={`w-1.5 h-4 rounded-full ${accentBar}`} />
                <span className="uppercase tracking-widest">{s.label}</span>
                <span className="opacity-80 font-medium">· {s.rows.length} prospek</span>
              </div>
              <span className="tabular-nums font-num">Subtotal {fmtIDR(s.subtotal)}</span>
            </button>
            {isOpen && (
              s.rows.length === 0 ? (
                <div className="text-xs text-[#A0A3A8] py-6 text-center">Tidak ada.</div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F5F7] text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">
                    <tr>
                      <th className="text-left px-5 py-2.5 w-10">No</th>
                      <th className="text-left px-3 py-2.5">Customer</th>
                      <th className="text-left px-3 py-2.5">Service</th>
                      <th className="text-left px-3 py-2.5 w-24">Bulan</th>
                      <th className="text-right px-3 py-2.5 w-40">Nilai</th>
                      <th className="text-center px-5 py-2.5 w-24">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                      {s.rows.map((r, i) => {
                        const val = r[s.valField];
                        const meta = RATE_META[r.rate] || RATE_META[1];
                        return (
                          <tr
                            key={r.id}
                            onClick={() => onRowClick && onRowClick(r.id)}
                            data-testid={`detail-row-${r.id}`}
                            className="border-t border-[#DCE0E7] cursor-pointer hover:bg-[#F4F5F7] transition-colors"
                          >
                            <td className="px-5 py-2.5 text-[#A0A3A8] font-num text-xs">{i + 1}</td>
                            <td className="px-3 py-2.5 font-semibold text-[#090808]">{r.customer}</td>
                            <td className="px-3 py-2.5 text-[#626061] truncate max-w-xs">{r.service}</td>
                            <td className="px-3 py-2.5 text-[#626061] text-xs whitespace-nowrap">{fmtMonth(r.pipeline_month)}</td>
                            <td className="px-3 py-2.5 text-right font-num text-xs font-bold text-[#090808] tabular-nums">{fmtIDR(val)}</td>
                            <td className="px-5 py-2.5 text-center">
                              <span className={`inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold ${meta.soft}`}>{meta.label}</span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )
            )}
          </div>
        );
      })}
    </Card>
  );
}

